'use strict';
/* global L */

const _ = require('lodash');
const mapConfig = _.cloneDeep(require('./PreviewMapConfig.js'));

/** @PdfPreviewStore */
const PdfPreviewStore = function ($timeout, APP_DISPATCHER_ID, Flux, IdentityService, PdfPreviewActions, ResourceHelper, Utils) {
  const PdfResource = ResourceHelper.getResource('DataExport');

  //Store init
  const PdfPreviewStore = Flux.Store.create({
    name: 'PdfPreviewStore',
    events: {
      Changed: true,
      Processing: true,
      Finished: true,
      AutodeskSynced: true,
      DownloadCanceled: true
    }
  });

  //Null initial state
  PdfPreviewStore.$state = {
    cancelPromise: null,
    dataExportId: null,
    dialogVisibility: false,
    showCancelDialog: false,
    uri: ''
  };

  /**
   * Constructor function that binds to the interaction Flux.Action Signal from the component
   */
  PdfPreviewStore.init = () => {
    const dispatcher = Flux.Dispatcher.get(APP_DISPATCHER_ID);

    dispatcher.register(PdfPreviewActions.ShowPdfPreview, this._handleShowPdfPreview.bind(this));
    dispatcher.register(PdfPreviewActions.HidePdfPreview, this._handleHidePdfPreview.bind(this));
    dispatcher.register(PdfPreviewActions.ShowPdfCancelDialog, this._handleShowPdfCancelDialog.bind(this));
    dispatcher.register(PdfPreviewActions.ProcessPdf, this._handlePdfProcessing.bind(this));
    dispatcher.register(PdfPreviewActions.CancelDownload, this._handleCancelDownload.bind(this));
  };

  const updateUI = (model) => {
    // We need to refresh the data, since the sync trigger comes from firebase
    // and such, the model is not updated here yet
    PdfResource.find(model.id).then((pdf) => {
      if (pdf.location) {
        PdfPreviewStore.$set('uri', pdf.location);
        PdfPreviewStore.emit(PdfPreviewStore.Events.Finished);
      }
    });
  };

  // Handlers //////////////////////////////////////////////////////////////////

  /**
   * Handles the visiblilty flag to show/hide the modal
   * Broken into two methods because when clearing the notification, the controller was reading this value and opening the modal again
   */
  this._handleShowPdfPreview = () => {
    PdfPreviewStore.$set('dialogVisibility', true);
    PdfPreviewStore.emit(PdfPreviewStore.Events.Changed);
  };

  this._handleHidePdfPreview = () => {
    PdfPreviewStore.$set('dialogVisibility', false);
    PdfPreviewStore.$set('showCancelDialog', false);
    PdfPreviewStore.emit(PdfPreviewStore.Events.Changed);
  };

  this._handleShowPdfCancelDialog = () => {
    PdfPreviewStore.$set('dialogVisibility', true);
    PdfPreviewStore.$set('showCancelDialog', true);
    PdfPreviewStore.emit(PdfPreviewStore.Events.Changed);
  };

  this._handlePdfProcessing = (data) => {
    const sanitizedData = this._sanitizeData(data.data);

    PdfResource.create(sanitizedData)
      .then((dataExport) => {
        PdfPreviewStore.$set('dataExportId', dataExport.id);
        PdfPreviewStore.emit(PdfPreviewStore.Events.Processing);

        if (dataExport.location) {
          PdfPreviewStore.$set('uri', dataExport.location.replace('http://s3-us-west-1.amazonaws.com/skycatch-pdf/', 'https://d1xes4epbabnon.cloudfront.net/'));
          PdfPreviewStore.emit(PdfPreviewStore.Events.Finished);
        }
        else {
          PdfResource.attachSyncListeners(dataExport.id, (model) => {
            updateUI(model);
          });
        }
      });
  };

  /*
   * Utility function that clears recursive objects, unneeded attributes and
   * makes data transformations, if any, to the object that will be saved as
   * data_export. In this case, the require properties are determined by the
   * object the octo-worker expects (NOT SPEC'ED), and is a very small subset
   * of properties.
   * Note that similar functions exists in DC, Annotation and MapStore that deal
   * with formatting, is probably a good idea to reformat them as presenters
   *  ~chubas
   */
  this._sanitizeData = (data) => {
    data = _.cloneDeep(data);
    const formula = data.formula;

    if (formula.annotations) {
      formula.annotations.forEach((annotation) => {
        if (annotation.layers) {
          annotation.layers.forEach((layer) => {
            delete layer.$$hashkey;
            delete layer.$$layerUID;
            delete layer._permissions;
            delete layer.migrationId;
            delete layer.position;
            delete layer.createdAt;
            delete layer.updatedAt;
            delete layer.annotation;
            delete layer.isDeleted;
          });
        }
      });
    }

    if (formula.dataCapture) {
      formula.dataCapture.forEach((dc) => {
        if (dc.layers) {
          dc.layers.forEach((layer) => {
            delete layer._permissions;
            delete layer.createdAt;
            delete layer.updatedAt;
            delete layer.isDeleted;
          });
        }
      });
    }

    if (formula.site) {
      delete formula.site.createdAt;
      delete formula.site.updatedAt;
      delete formula.site.isDeleted;
    }

    return data;
  };

  this._handleCancelDownload = () => {
    // hit update endpoint to cancel job on backend
    // then detach sync listeners to prevent UI from updating
    const id = PdfPreviewStore.get('dataExportId');
    PdfResource.update(id, {cancel: true});
    PdfResource.detachSyncListeners(id);
    PdfPreviewStore.emit(PdfPreviewStore.Events.DownloadCanceled);
  };

  /**
   * Utility function to create a booster compatible site data
   */
  this.cookSitePreviewMap = (site, defaultDataCapture) => {
    let datacaptures;
    const mapUtil = Utils.Map.create(mapConfig);
    const location = _.clone(site.location);
    const bounds = location.bounds;

    location.bounds = L.latLngBounds([bounds.bottom, bounds.left], [bounds.top, bounds.right]);
    mapConfig.mapCenter = _.pick(location, ['lat', 'lng', 'zoom']);

    if (defaultDataCapture) {
      // TODO: Refactor, this is the same format as MapController#datacaptures
      const formattedDataCapture = _.cloneDeep(defaultDataCapture);
      formattedDataCapture.layers = formattedDataCapture.dataCaptureLayers;
      formattedDataCapture.layers.forEach((layer) => {
        layer.id = layer.layerData;
      });
      datacaptures = [formattedDataCapture];
    }

    return {
      id: site.id,
      center: mapConfig.mapCenter,
      defaults: mapUtil.getMapDefaults(),
      events: mapConfig.events,
      datacaptures: datacaptures
    };
  };

  return PdfPreviewStore;
};

PdfPreviewStore.$inject = ['$timeout', 'APP_DISPATCHER_ID', 'sbFlux', 'IdentityService', 'PdfPreviewActions', 'ResourceHelper', 'Utils'];

module.exports = PdfPreviewStore;



// WEBPACK FOOTER //
// ./src/app/shared/pdfPreview/PdfPreviewStore.js